<template>
    <app-layout v-if="isPageReady">
        <common-loader :isProcessing="isFlowInitializing"></common-loader>
        <common-content-spacer v-if="!isFlowInitializing && isFlowReady" height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
            </common-content-spacer>

            <referrals-confirmation
                v-if="isProposedTimeSent"
                :heading="$t('pages.referrals.new.steps.confirmation.time_slots_proposed')"
                type="success"
                :centerHeading="true"
                headingStyle="plain"
                class="referral-pool-confirmation"
            >
                <template #content>
                    <common-content-spacer height="small" class="confirmation">
                        <common-page-header
                            :heading="$t('pages.referrals.pool.proposed_slots')"
                            size="small"
                            contentSpacing="tiny"
                            headingStyle="plain"
                        ></common-page-header>
                        <referral-time-slots-preview :groupSlotsByDate="selectedSlotsByDate"></referral-time-slots-preview>
                        <common-buttons>
                            <common-button
                                :label="$t('pages.auth.signup.steps.congratulations.content.button.proceed_to_dashboard')"
                                size="large"
                                type="primary"
                                @click="$router.push({ name: 'index.home' })"
                            ></common-button>
                        </common-buttons>
                    </common-content-spacer>
                </template>
            </referrals-confirmation>

            <common-page-split v-else :reverse="true" :showAside="false">
                <template #aside>
                    <common-content-spacer height="tiny">
                        <common-content-group>
                            <common-content-spacer height="tiny">
                                <referral-time-slots-preview
                                    :groupSlotsByDate="selectedSlotsByDate"
                                    :showDeleteIcon="true"
                                    @remove-item="onRemoveItem"
                                ></referral-time-slots-preview>

                                <common-buttons>
                                    <common-button
                                        :label="$t('pages.referrals.pool.send_to_patient_provider')"
                                        :isSubmittable="false"
                                        :isDisabled="sharedState.totalSlotCount < 3"
                                        size="medium"
                                        type="primary"
                                        :isProcessing="isSending"
                                        @click="sendToPatient()"
                                    ></common-button>
                                </common-buttons>
                            </common-content-spacer>
                        </common-content-group>
                    </common-content-spacer>
                </template>

                <template #main>
                    <common-content-wrapper width="gigantic">
                        <div class="propose-time-calendar">
                            <common-page-header
                                :heading="$t('pages.referrals.pool.please_select_3_time_slots')"
                                size="small"
                                contentSpacing="medium"
                            ></common-page-header>

                            <availability-topnav-content
                                :currentDate="calendarDate"
                                :view="calendarView"
                                :onDateChange="changeCalendarDate"
                                :showLocationSelector="false"
                                :showManageButton="false"
                                :showViewToggle="false"
                            ></availability-topnav-content>

                            <div class="propose-time-calendar__send">
                                <p v-if="remainingSlotsHint" class="propose-time-calendar__remaining">
                                    {{ remainingSlotsHint }}
                                </p>
                                <common-button
                                    :label="$t('pages.referrals.pool.send_to_patient_provider')"
                                    :isSubmittable="false"
                                    :isDisabled="sharedState.totalSlotCount < 3"
                                    size="medium"
                                    type="primary"
                                    :isProcessing="isSending"
                                    @click="sendToPatient()"
                                ></common-button>
                            </div>

                            <availability-schedule
                                :user="scheduleUser"
                                :practice="schedulePractice"
                                :practiceLocationId="proposePracticeLocationId"
                                :currentDate="calendarDate"
                            ></availability-schedule>
                        </div>
                    </common-content-wrapper>
                </template>
            </common-page-split>
        </common-content-spacer>

        <available-locations-modal
            v-if="showAvailableLocationsModal"
            :referral="referral"
            :locations="locations"
            @go-to-location="onProposeLocationSelected"
            @close="onProposeLocationsModalClose"
        ></available-locations-modal>
        <common-modal v-if="isReasonCodeModalOpen" width="small" @close="closeReasonCodeModal">
            <header class="match-modal-header">
                <h4>{{ reasonConfig.heading }}</h4>
            </header>
            <div class="reason-code-modal__content">
                <p>{{ reasonMessage }}</p>
            </div>
            <div class="match-modal-footer">
                <common-button
                    v-if="reasonConfig.action"
                    :label="reasonConfig.button"
                    type="secondary"
                    size="medium"
                    @click="runReasonAction()"
                ></common-button>
            </div>
        </common-modal>
        <common-modal v-if="isPracticeCreationOpen" width="small" @close="togglePracticeCreationModal">
            <header class="match-modal-header">
                <h4>{{ $t('pages.practice.create.create_practice.heading') }}</h4>
            </header>
            <div class="match-modal-footer">
                <common-button
                    :label="$t('pages.practice.create.create_practice.button')"
                    type="primary"
                    size="large"
                    @click="goToCreatePractice()"
                ></common-button>
            </div>
        </common-modal>
    </app-layout>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex'
    import AppLayout from 'components/layouts/AppLayout'
    import AvailabilitySchedule from 'components/page-elements/AvailabilitySchedule'
    import AvailabilityTopnavContent from 'components/layouts/app-layout/AvailabilityTopnavContent'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import dayjs from 'dayjs'
    // import emitter from 'tiny-emitter/instance'
    import UsersAPI from 'api/UsersAPI'
    import PatientAPI from 'api/PatientAPI'
    import ReferralTimeSlotsPreview from 'components/page-elements/referrals/ReferralTimeSlotsPreview.vue'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PracticesAPI from 'api/PracticesAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralPoolProposeFlowMixin from 'mixins/ReferralPoolProposeFlowMixin'
    import AvailableLocationsModal from 'components/page-elements/referrals/AvailableLocationsModal.vue'

    // Calendar month/day navigation updates route query params, which re-runs
    // loadData via beforeRouteUpdate; cache the physician so those navigations
    // don't refetch and re-trigger the full-page loader.
    let physicianCache = null

    export default {
        mixins: [
            PageWithTitleMixin,
            PageWithElementsMixin,
            PageWithDataMixin,
            ReferralsNewPageMixin,
            ScreenSizeDetectionMixin,
            ReferralPoolProposeFlowMixin,
        ],
        components: {
            AppLayout,
            AvailabilitySchedule,
            AvailabilityTopnavContent,
            ReferralTimeSlotsPreview,
            AvailableLocationsModal,
        },

        data() {
            return {
                physician: {},
                patient: {},
                practice: {},
                selectedSlotsByDate: {},
                sharedState: {
                    totalSlotCount: 0,
                },
                isSending: false,
                isProposedTimeSent: false,
            }
        },

        provide() {
            return {
                isReferralPool: true,
                isProposeFlow: true,
                sharedState: this.sharedState,
            }
        },

        loadData(self) {
            if (self.$route.query.pendingInit === '1') {
                return {
                    patient: Promise.resolve({}),
                    physician: Promise.resolve({}),
                    practice: Promise.resolve({}),
                }
            }

            if (self.$route.query.isFromPatient) {
                return {
                    patient: PatientAPI.getPatientDetails(self.$route.query.patientId, self.$auth.getUser().id),
                    physician: {},
                    practice: {},
                }
            } else if (self.$route.query.practiceType === PracticeTypeEnum.PHYSICIAN) {
                const userId = self.$route.query.userId
                return {
                    physician:
                        physicianCache && String(physicianCache.id) === String(userId)
                            ? Promise.resolve(physicianCache)
                            : UsersAPI.get(userId).then((physician) => {
                                  physicianCache = physician
                                  return physician
                              }),
                    patient: {},
                    practice: {},
                }
            } else {
                return {
                    patient: {},
                    physician: {},
                    practice: PracticesAPI.getPracticeById(self.$route.query.practiceId),
                }
            }
        },

        computed: {
            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.pool.breadcrumbs.referrals'),
                        route: { name: `referrals.pool.received` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.received`),
                        route: { name: `referrals.pool.received` },
                    },
                    {
                        label: this.$t('pages.referrals.pool.current_referral'),
                        route: { name: `referrals.received.pool.show`, params: { id: this.$route.params.poolId } },
                    },
                    {
                        label: this.$t('pages.referrals.pool.propose_time'),
                    },
                ]
            },

            ...mapGetters('availability', { storeSelectedSlots: 'selectedSlots', maxSlots: 'maxSlots' }),

            remainingSlotsToSelect() {
                return Math.max(0, (this.maxSlots || 3) - (this.sharedState.totalSlotCount || 0))
            },

            // Only after at least one pick — "Select 3 more" with 0 selected is nonsense.
            remainingSlotsHint() {
                if (this.sharedState.totalSlotCount < 1 || this.remainingSlotsToSelect < 1) {
                    return null
                }
                return this.$t('layouts.app.availability_topnav.select_more', { count: this.remainingSlotsToSelect })
            },

            calendarDate() {
                return this.$route.query.date ? dayjs(this.$route.query.date).toDate() : new Date()
            },

            calendarView() {
                return this.$route.query.view || 'monthly'
            },

            scheduleUser() {
                return this.physician && this.physician.id
                    ? this.physician
                    : { id: Number(this.$route.query.userId) || this.$auth.getActiveMember()?.id || this.$auth.getUser()?.id }
            },

            schedulePractice() {
                return this.practice && this.practice.id
                    ? this.practice
                    : { id: Number(this.$route.query.practiceId) || this.$auth.getPractice()?.id }
            },

            proposePracticeLocationId() {
                return Number(this.$route.params.locationId) || 0
            },
        },

        methods: {
            ...mapMutations('availability', ['SET_SELECT_MODE', 'SET_MAX_SLOTS', 'TOGGLE_SLOT']),

            changeCalendarDate(date) {
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            date: dayjs(date).format('YYYY-MM-DD'),
                        },
                    })
                    .catch(() => {})
            },

            syncSelectedSlotsFromStore(slots) {
                const grouped = {}
                slots
                    .slice()
                    .sort((a, b) => dayjs(`${a.date} ${a.startTime}`) - dayjs(`${b.date} ${b.startTime}`))
                    .forEach((slot) => {
                        const entry = {
                            label: `${slot.startTime} - ${slot.endTime}`,
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        }
                        if (!grouped[slot.date]) {
                            this.$set(grouped, slot.date, [])
                        }
                        grouped[slot.date].push(entry)
                    })

                this.selectedSlotsByDate = grouped
                this.updateSlotCount()
            },

            slotChanged(payload) {
                const date = payload.date
                const updatedSlots = payload.slots.map((slot) => ({
                    label: slot.label,
                    date: slot.value.date,
                    startTime: slot.value.startTime,
                    endTime: slot.value.endTime,
                    from: slot.value.from,
                    to: slot.value.to,
                }))

                updatedSlots.sort((a, b) => {
                    const dateA = dayjs(`${a.date} ${a.startTime}`)
                    const dateB = dayjs(`${b.date} ${b.startTime}`)
                    return dateA - dateB
                })
                this.$set(this.selectedSlotsByDate, date, updatedSlots)
                const sortedSlotsByDate = {}
                Object.keys(this.selectedSlotsByDate)
                    .sort((a, b) => dayjs(a).unix() - dayjs(b).unix())
                    .forEach((key) => {
                        sortedSlotsByDate[key] = this.selectedSlotsByDate[key]
                    })

                this.selectedSlotsByDate = sortedSlotsByDate
                this.updateSlotCount()
            },

            formatDate(dateStr) {
                return dayjs(dateStr).format('DD MMMM YYYY')
            },
            formatTime(timeStr) {
                return dayjs(timeStr, 'HH:mm').format('hh:mm A')
            },

            updateSlotCount() {
                this.sharedState.totalSlotCount = Object.values(this.selectedSlotsByDate).reduce(
                    (total, slots) => total + slots.length,
                    0,
                )
            },

            onRemoveItem(payload) {
                this.removeItem(payload.slot, payload.date)
            },

            removeItem(slot) {
                this.TOGGLE_SLOT({ date: slot.date, startTime: slot.startTime, endTime: slot.endTime })
            },

            sendToPatient() {
                const proposedTimeSlots = []
                Object.values(this.selectedSlotsByDate).forEach((slots) => {
                    slots.forEach((slot) => {
                        proposedTimeSlots.push({
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        })
                    })
                })

                const request = {
                    practiceLocationId: Number(this.$route.params.locationId),
                    proposedTimeSlots: proposedTimeSlots,
                    distance: this.$route.query.distance,
                    userId: this.$auth.getActiveMember().id,
                }
                this.isSending = true
                ReferralsPoolAPI.sendProposeTimeSlots(this.$route.params.poolId, request)
                    .then(() => {
                        this.isProposedTimeSent = true
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },
        },

        watch: {
            storeSelectedSlots(slots) {
                this.syncSelectedSlotsFromStore(slots)
            },
        },

        mounted() {
            this.SET_MAX_SLOTS(3)
            this.SET_SELECT_MODE(true)
            this.initProposeTimeFlow()
        },

        beforeDestroy() {
            this.SET_SELECT_MODE(false)
        },
    }
</script>
<style lang="scss">
    .confirmation {
        display: flex;
        align-items: center;
        flex-direction: column;
        text-align: center;
    }
    .referral-pool-confirmation {
        .referral-confirmation__heading {
            max-width: 50rem;

            .page-header__heading {
                justify-content: center;
                font-size: rem(22px);
            }
        }
    }
</style>
